import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { ADMIN_CALCULATOR_SHARE_LINKS_QUERY } from '../../graphql/queries';
import {
  ADMIN_CREATE_CALCULATOR_SHARE_LINK_MUTATION, ADMIN_REVOKE_CALCULATOR_SHARE_LINK_MUTATION,
} from '../../graphql/mutations';
import Pagination from '../../components/common/Pagination';
import styles from './CalculatorShareLinks.module.css';

interface ShareLink {
  id: string; token: string; label: string; createdAt: string; createdBy: string;
  expiresAt: string | null; revokedAt: string | null; views: number; lastViewedAt: string | null; status: string;
}

export const calculatorShareUrl = (token: string) => `${window.location.origin}/share/calculator/${token}`;

const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-GB', {
  day: 'numeric', month: 'short', year: 'numeric',
}) : '');

// expiresAt is the start of the day AFTER the chosen date (UTC), so show the last valid day, in UTC.
const lastDay = (iso: string) => new Date(new Date(iso).getTime() - 1).toLocaleDateString('en-GB', {
  day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
});

const STATUS_LABEL: Record<string, string> = { active: 'Active', expired: 'Expired', revoked: 'Revoked' };

const LINKS_PAGE = 10;

const CalculatorShareLinks = () => {
  const [page, setPage] = useState(1);
  const { data, loading, error } = useQuery(ADMIN_CALCULATOR_SHARE_LINKS_QUERY, {
    variables: { page, limit: LINKS_PAGE },
    fetchPolicy: 'cache-and-network',
  });
  const [label, setLabel] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [create, { loading: creating }] = useMutation(ADMIN_CREATE_CALCULATOR_SHARE_LINK_MUTATION, {
    refetchQueries: ['AdminCalculatorShareLinks'],
    onCompleted: () => { setLabel(''); setExpiresAt(''); setFormError(null); setPage(1); },
    onError: (e) => setFormError(e.message),
  });
  const [revoke] = useMutation(ADMIN_REVOKE_CALCULATOR_SHARE_LINK_MUTATION, {
    refetchQueries: ['AdminCalculatorShareLinks'],
    onError: (e) => setFormError(e.message),
  });
  const links = (data?.adminCalculatorShareLinks?.links ?? []) as ShareLink[];
  const total: number = data?.adminCalculatorShareLinks?.total ?? 0;
  const today = new Date().toISOString().slice(0, 10);

  const copy = async (token: string) => {
    try {
      await navigator.clipboard.writeText(calculatorShareUrl(token));
      setCopied(token);
      setTimeout(() => setCopied((c) => (c === token ? null : c)), 2000);
    } catch {
      setFormError('The link could not be copied; select it and copy it by hand.');
    }
  };

  return (
    <div className={styles.panel}>
      <h3 className={styles.title}>Share the cost calculator</h3>
      <p className={styles.note}>
        Anyone with a link can use the calculator with every input and result, at the Unit prices
        above. They do not see how many real jobs or viewer sessions the models were fitted from.
      </p>
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          setFormError(null);
          void create({ variables: { label: label.trim() || null, expiresAt: expiresAt || null } });
        }}
      >
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Name</span>
          <input
            className="input-field"
            value={label}
            maxLength={120}
            placeholder="e.g. Acme Surveying"
            onChange={(e) => setLabel(e.target.value)}
          />
        </label>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Valid until (optional)</span>
          <input
            className="input-field"
            type="date"
            min={today}
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
        </label>
        <button type="submit" className="btn-primary py-2 px-5 rounded-lg disabled:opacity-70" disabled={creating}>
          {creating ? 'Creating…' : 'Create link'}
        </button>
      </form>
      {formError && <p className={styles.error} role="alert">{formError}</p>}
      {error && <p className={styles.error} role="alert">The links could not be loaded: {error.message}</p>}

      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead>
            <tr><th>Link</th><th>Created</th><th>Valid until</th><th>Views</th><th>Status</th><th /></tr>
          </thead>
          <tbody>
            {!loading && links.length === 0 && (
              <tr><td colSpan={6} className={styles.empty}>No links yet</td></tr>
            )}
            {links.map((l) => (
              <tr key={l.id}>
                <td>
                  <span className={styles.strong}>{l.label || 'Untitled'}</span>
                  <span className={styles.url}>{calculatorShareUrl(l.token)}</span>
                </td>
                <td>
                  {day(l.createdAt)}
                  <span className={styles.sub}>{l.createdBy}</span>
                </td>
                <td>{l.expiresAt ? lastDay(l.expiresAt) : 'No end date'}</td>
                <td>
                  {l.views.toLocaleString('en-US')}
                  {l.lastViewedAt && <span className={styles.sub}>last {day(l.lastViewedAt)}</span>}
                </td>
                <td><span className={`${styles.chip} ${styles[l.status] ?? ''}`}>{STATUS_LABEL[l.status] ?? l.status}</span></td>
                <td className={styles.actions}>
                  {l.status === 'active' && (
                    <>
                      <button type="button" className={styles.btn} onClick={() => void copy(l.token)}>
                        <span className="material-symbols-outlined text-sm">{copied === l.token ? 'check' : 'content_copy'}</span>
                        {copied === l.token ? 'Copied' : 'Copy'}
                      </button>
                      <button
                        type="button"
                        className={`${styles.btn} ${styles.danger}`}
                        onClick={() => {
                          if (window.confirm(`Revoke "${l.label || 'Untitled'}"? The link stops working at once.`)) {
                            void revoke({ variables: { id: l.id } });
                          }
                        }}
                      >
                        Revoke
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination page={page} pageSize={LINKS_PAGE} total={total} onPageChange={setPage} noun="links" variant="inline" />
    </div>
  );
};

export default CalculatorShareLinks;
